import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { ItemArrastre } from './useArrastre';

/**
 * Arrastre por PUNTERO para el compositor — el que sí funciona con el dedo.
 *
 * El compositor se movía con arrastre nativo de HTML, que en un teléfono
 * directamente no ocurre: componer un proceso desde el móvil era imposible.
 * Esta vía lo resuelve sin librerías y sin tocar la lógica de negocio: los
 * destinos ya validaban contra el ítem en vuelo que vive en estado de React,
 * no contra `dataTransfer`, así que basta con alimentar ese mismo estado.
 *
 * El gesto es el de las listas reordenables de iOS, y no por copiar: resuelve
 * el conflicto real entre desplazar y arrastrar. Un dedo que baja sobre una
 * ficha puede querer las dos cosas, y no hay forma de saberlo en el primer
 * instante. Entonces:
 *
 *   · Se sostiene 250 ms sin moverse → la ficha se LEVANTA (con su vibración,
 *     porque el momento de levantar es el que hay que confirmar).
 *   · Si el dedo se mueve antes de eso, era desplazamiento: no pasa nada y la
 *     lista se desliza como siempre.
 *   · Levantada, la ficha va pegada al dedo uno a uno, y el destino bajo el
 *     dedo se ilumina mientras se pasa por encima.
 *   · Se suelta encima de una sesión y cae ahí; en cualquier otro lado, vuelve
 *     sin hacer nada.
 *
 * Convive con el arrastre nativo en vez de reemplazarlo: en el escritorio el
 * nativo ya funciona y tiene el comportamiento que la gente espera del sistema.
 */

/** Cuánto hay que sostener para levantar. Menos se dispara al desplazar. */
const SOSTENER_MS = 250;
/** Cuánto puede temblar el dedo antes de que se cuente como desplazamiento. */
const TOLERANCIA_PX = 8;

/** Atributo con el que un destino se anuncia: lo busca `elementFromPoint`. */
export const ATRIBUTO_DESTINO = 'data-destino-sesion';

export interface ArrastrePuntero {
  /** Ítem levantado, o null. Alimenta el mismo estado que el arrastre nativo. */
  levantado: ItemArrastre | null;
  /** Id de la sesión bajo el dedo ahora mismo, para iluminarla. */
  sobre: string | null;
  /** Dónde pintar el fantasma que sigue al dedo. */
  posicion: { x: number; y: number } | null;
  /** Qué dice el fantasma. */
  etiqueta: string | null;
  /** Handler de `onPointerDown` para un origen arrastrable. */
  tomar: (item: ItemArrastre, etiqueta: string) => (e: ReactPointerEvent) => void;
  /** Cada destino registra acá qué hacer cuando algo cae encima. */
  registrarDestino: (id: string, soltar: (item: ItemArrastre) => void) => () => void;
}

export function useArrastrePuntero(): ArrastrePuntero {
  const [levantado, setLevantado] = useState<ItemArrastre | null>(null);
  const [sobre, setSobre] = useState<string | null>(null);
  const [posicion, setPosicion] = useState<{ x: number; y: number } | null>(null);
  const [etiqueta, setEtiqueta] = useState<string | null>(null);

  const destinos = useRef(new Map<string, (item: ItemArrastre) => void>());
  const gesto = useRef<{
    item: ItemArrastre;
    etiqueta: string;
    inicio: { x: number; y: number };
    temporizador: number | null;
    levantado: boolean;
    elemento: Element;
    pointerId: number;
  } | null>(null);

  const registrarDestino = useCallback(
    (id: string, soltar: (item: ItemArrastre) => void) => {
      destinos.current.set(id, soltar);
      return () => {
        destinos.current.delete(id);
      };
    },
    [],
  );

  const limpiar = useCallback(() => {
    const g = gesto.current;
    if (g?.temporizador) window.clearTimeout(g.temporizador);
    if (g?.levantado) {
      try {
        (g.elemento as HTMLElement).releasePointerCapture?.(g.pointerId);
      } catch {
        /* el navegador ya lo soltó */
      }
    }
    gesto.current = null;
    setLevantado(null);
    setSobre(null);
    setPosicion(null);
    setEtiqueta(null);
  }, []);

  /** Qué sesión hay debajo de este punto de la pantalla. */
  const destinoEn = (x: number, y: number): string | null => {
    const el = document.elementFromPoint(x, y);
    const contenedor = el?.closest(`[${ATRIBUTO_DESTINO}]`);
    return contenedor?.getAttribute(ATRIBUTO_DESTINO) ?? null;
  };

  const tomar = useCallback(
    (item: ItemArrastre, etiquetaItem: string) => (e: ReactPointerEvent) => {
      // El ratón ya tiene el arrastre nativo, que es el que la gente espera en
      // escritorio. Esta vía es para el dedo y el lápiz.
      if (e.pointerType === 'mouse') return;
      if (gesto.current) return;

      const elemento = e.currentTarget as HTMLElement;
      const pointerId = e.pointerId;
      const inicio = { x: e.clientX, y: e.clientY };

      const temporizador = window.setTimeout(() => {
        const g = gesto.current;
        if (!g) return;
        g.levantado = true;
        try {
          elemento.setPointerCapture(pointerId);
        } catch {
          /* sin captura el gesto sigue funcionando mientras no salga del nodo */
        }
        // Se levantó: confirmalo en la mano antes que en la vista.
        try {
          navigator.vibrate?.(12);
        } catch {
          /* sin vibración no pasa nada */
        }
        setLevantado(g.item);
        setEtiqueta(g.etiqueta);
        setPosicion(inicio);
      }, SOSTENER_MS);

      gesto.current = {
        item,
        etiqueta: etiquetaItem,
        inicio,
        temporizador,
        levantado: false,
        elemento,
        pointerId,
      };
    },
    [],
  );

  /**
   * Con algo levantado, la página deja de desplazarse.
   *
   * Sin esto el documento se desliza bajo el dedo mientras se arrastra —el
   * navegador ya decidió que el gesto era un desplazamiento y ningún
   * `preventDefault` posterior lo convence—, y la ficha termina cayendo en una
   * sesión que ni siquiera es la que se estaba mirando. Se restaura solo al
   * soltar, incluso si el gesto se canceló.
   */
  useEffect(() => {
    if (!levantado) return;
    const cuerpo = document.body;
    const previo = {
      touchAction: cuerpo.style.touchAction,
      userSelect: cuerpo.style.userSelect,
    };
    cuerpo.style.touchAction = 'none';
    cuerpo.style.userSelect = 'none';
    return () => {
      cuerpo.style.touchAction = previo.touchAction;
      cuerpo.style.userSelect = previo.userSelect;
    };
  }, [levantado]);

  // Los tres escuchas viven en la ventana y no en el nodo: una vez levantada la
  // ficha, el dedo se va a ir muy lejos del elemento donde empezó.
  useEffect(() => {
    const mover = (e: PointerEvent) => {
      const g = gesto.current;
      if (!g || e.pointerId !== g.pointerId) return;

      if (!g.levantado) {
        // Todavía decidiendo: si se movió, era desplazamiento. Se abandona sin
        // ruido y la lista sigue deslizándose como si nada.
        const dx = Math.abs(e.clientX - g.inicio.x);
        const dy = Math.abs(e.clientY - g.inicio.y);
        if (dx > TOLERANCIA_PX || dy > TOLERANCIA_PX) limpiar();
        return;
      }

      // Levantada: el fantasma va pegado al dedo, sin transición de por medio.
      e.preventDefault();
      setPosicion({ x: e.clientX, y: e.clientY });
      setSobre(destinoEn(e.clientX, e.clientY));
    };

    const soltar = (e: PointerEvent) => {
      const g = gesto.current;
      if (!g || e.pointerId !== g.pointerId) return;
      if (g.levantado) {
        const id = destinoEn(e.clientX, e.clientY);
        const accion = id ? destinos.current.get(id) : null;
        if (accion) accion(g.item);
      }
      limpiar();
    };

    const cancelar = (e: PointerEvent) => {
      if (gesto.current && e.pointerId === gesto.current.pointerId) limpiar();
    };

    const escapar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') limpiar();
    };

    window.addEventListener('pointermove', mover, { passive: false });
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', cancelar);
    window.addEventListener('keydown', escapar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', cancelar);
      window.removeEventListener('keydown', escapar);
    };
  }, [limpiar]);

  return { levantado, sobre, posicion, etiqueta, tomar, registrarDestino };
}
